import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import ExpandableRichText from '../../components/ExpandableRichText/ExpandableRichText';
import FactionMemberCard from '../../components/FactionMemberCard/FactionMemberCard';
import FactionMemberNoteModal from '../../components/FactionMemberNoteModal/FactionMemberNoteModal';
import FactionRpScenesPanel from '../../components/FactionRpScenesPanel/FactionRpScenesPanel';
import { useAuth } from '../../contexts/AuthContext';
import factionService from '../../services/factionService';
import { npcApi } from '../../services/characterApi';
import { getAlignmentMeta, getScopeMeta } from '../../utils/factionMeta';
import api from '../../services/api';
import styles from './Factions.module.css';

const flattenNpcs = (data) => {
  if (!data) return [];
  const items = [];
  (data.contentByUniverse || []).forEach((universeData) => {
    items.push(...(universeData.mainContent?.npcs || []));
    Object.values(universeData.elseworlds || {}).forEach((elseworld) => {
      items.push(...(elseworld.npcs || []));
    });
  });
  items.push(...(data.contentWithoutUniverse?.npcs || []));
  return items;
};

const ScopeIcon = ({ icon }) => {
  if (icon === 'globe') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    );
  }
  if (icon === 'flag') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </svg>
    );
  }
  if (icon === 'pin') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
  );
};

const FactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [faction, setFaction] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [myApplicableCharacters, setMyApplicableCharacters] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState('');
  const [addCharacterSearch, setAddCharacterSearch] = useState('');
  const [selectedApplicantCharacter, setSelectedApplicantCharacter] = useState('');
  const [applicantSearch, setApplicantSearch] = useState('');
  const [applications, setApplications] = useState([]);
  const [selectedNpc, setSelectedNpc] = useState('');
  const [newNpcName, setNewNpcName] = useState('');
  const [newNpcOccupation, setNewNpcOccupation] = useState('');
  const [newNpcAvatarUrl, setNewNpcAvatarUrl] = useState('');
  const [uploadingNpcAvatar, setUploadingNpcAvatar] = useState(false);
  const createNpcAvatarInputRef = useRef(null);
  const [createNpcModalOpen, setCreateNpcModalOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [editingRoleFor, setEditingRoleFor] = useState(null);
  const [roleDraft, setRoleDraft] = useState('');
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeMemberTab, setActiveMemberTab] = useState('characters');
  const [noteModal, setNoteModal] = useState({ open: false, member: null, memberType: 'character' });
  const [noteSaving, setNoteSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const factionData = await factionService.getFaction(id);

      let npcsData = null;
      let myApplicableCharactersData = { characters: [] };
      let charactersData = { characters: [] };
      let applicationsData = { applications: [] };

      if (user) {
        [npcsData, myApplicableCharactersData, charactersData, applicationsData] = await Promise.all([
          npcApi.getNpcs(),
          factionService.getMyApplicableCharacters(id),
          factionData.canEdit ? factionService.getAvailableCharacters(id) : Promise.resolve({ characters: [] }),
          factionData.canEdit ? factionService.getApplications(id) : Promise.resolve({ applications: [] }),
        ]);
      }

      setFaction(factionData);
      setCharacters(charactersData?.characters || []);
      setMyApplicableCharacters(myApplicableCharactersData?.characters || []);
      setNpcs(flattenNpcs(npcsData));
      setApplications(applicationsData?.applications || factionData.applications || []);
    } catch (err) {
      setError('Erreur lors du chargement de la faction');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id, user?.id]);

  const sections = useMemo(
    () => (faction?.sections || []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [faction?.sections]
  );

  useEffect(() => {
    if (!sections.length) {
      setActiveSectionId(null);
      return;
    }
    if (!sections.some((section) => String(section.id ?? section.title) === String(activeSectionId))) {
      setActiveSectionId(sections[0].id ?? sections[0].title);
    }
  }, [sections, activeSectionId]);

  const activeSection = useMemo(
    () => sections.find((section) => String(section.id ?? section.title) === String(activeSectionId)) || sections[0] || null,
    [sections, activeSectionId]
  );

  const alignmentMeta = getAlignmentMeta(faction?.alignment);
  const scopeMeta = getScopeMeta(faction?.scope);

  useEffect(() => {
    if (!createNpcModalOpen) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setCreateNpcModalOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [createNpcModalOpen]);

  const openCreateNpcModal = () => {
    setNewNpcName('');
    setNewNpcOccupation('');
    setNewNpcAvatarUrl('');
    setCreateNpcModalOpen(true);
  };

  const handleNpcAvatarFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    setUploadingNpcAvatar(true);
    setFeedback('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/api/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data?.url) {
        setNewNpcAvatarUrl(response.data.url);
      }
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors de l’upload de l’image.');
    } finally {
      setUploadingNpcAvatar(false);
      if (createNpcAvatarInputRef.current) {
        createNpcAvatarInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (loading) return;
    if (window.location.hash !== '#postuler') return;
    setActiveMemberTab('applications');
    const applySection = document.getElementById('faction-members-section');
    if (applySection) {
      applySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading]);

  const availableCharacters = useMemo(() => {
    const existingIds = new Set((faction?.characters || []).map((character) => character.id));
    const factionUniverseId = faction?.universe?.id ?? null;
    return characters
      .filter((character) => {
        if (!factionUniverseId) return true;
        return character.universe?.id === factionUniverseId;
      })
      .filter((character) => !existingIds.has(character.id))
      .filter((character) => character.name.toLowerCase().includes(addCharacterSearch.toLowerCase()));
  }, [characters, faction, addCharacterSearch]);

  const availableNpcs = useMemo(() => {
    const existingIds = new Set((faction?.npcs || []).map((npc) => npc.id));
    return npcs
      .filter((npc) => !existingIds.has(npc.id))
      .filter((npc) => npc.name.toLowerCase().includes(memberSearch.toLowerCase()));
  }, [npcs, faction, memberSearch]);

  const applicableCharacters = useMemo(() => {
    const factionUId = faction?.universe?.id ?? null;
    return (myApplicableCharacters || [])
      .filter((character) => character.kind !== 'event')
      .filter((character) => {
        if (factionUId == null) return true;
        const uid = character.universe?.id;
        return uid != null && Number(uid) === Number(factionUId);
      })
      .filter((character) =>
        character.name.toLowerCase().includes(applicantSearch.toLowerCase())
      );
  }, [myApplicableCharacters, applicantSearch, faction?.universe?.id]);

  const filteredFactionCharacters = useMemo(() => {
    return (faction?.characters || []).filter((character) =>
      character.name.toLowerCase().includes(memberSearch.toLowerCase())
    );
  }, [faction, memberSearch]);

  const filteredFactionNpcs = useMemo(() => {
    return (faction?.npcs || []).filter((npc) =>
      npc.name.toLowerCase().includes(memberSearch.toLowerCase())
    );
  }, [faction, memberSearch]);

  const getStatusLabel = (status) => {
    if (status === 'open') return 'Ouverte';
    if (status === 'closed') return 'Fermée';
    return status || 'Inconnu';
  };

  const handleAddCharacter = async () => {
    if (!selectedCharacter) return;
    try {
      setActionLoading(true);
      await factionService.addCharacterMember(id, selectedCharacter);
      setSelectedCharacter('');
      setAddCharacterSearch('');
      setFeedback('Personnage ajouté avec succès.');
      await loadData();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors de l’ajout du personnage.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNpc = async () => {
    if (!selectedNpc) return;
    try {
      setActionLoading(true);
      await factionService.addNpcMember(id, selectedNpc);
      setSelectedNpc('');
      setFeedback('PNJ ajouté avec succès.');
      await loadData();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors de l’ajout du PNJ.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateAndAddNpc = async () => {
    const name = newNpcName.trim();
    if (!name) {
      setFeedback('Indique un nom pour le PNJ.');
      return;
    }
    try {
      setActionLoading(true);
      setFeedback('');
      const payload = { name };
      const occ = newNpcOccupation.trim();
      if (occ) {
        payload.occupation = occ;
      }
      const avatar = newNpcAvatarUrl.trim();
      if (avatar) {
        payload.avatar = avatar;
      }
      const created = await factionService.createFactionNpc(id, payload);
      const npcId = created?.id;
      if (!npcId) {
        setFeedback('Réponse serveur inattendue lors de la création du PNJ.');
        return;
      }
      setNewNpcName('');
      setNewNpcOccupation('');
      setNewNpcAvatarUrl('');
      setCreateNpcModalOpen(false);
      setFeedback('PNJ de faction créé et validé (utilisable en mission RP). Il n’apparaît pas dans « Mes PNJ ».');
      await loadData();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors de la création ou de l’ajout du PNJ.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApplyToFaction = async () => {
    if (!selectedApplicantCharacter) return;
    try {
      setActionLoading(true);
      await factionService.applyToFaction(id, selectedApplicantCharacter);
      setSelectedApplicantCharacter('');
      setApplicantSearch('');
      setFeedback('Candidature envoyée avec succès.');
      await loadData();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors de l’envoi de la candidature.');
    } finally {
      setActionLoading(false);
    }
  };

  const showApplySection = Boolean(user) && (!faction?.canEdit || applicableCharacters.length > 0);

  const handleRemoveCharacter = async (characterId) => {
    try {
      setActionLoading(true);
      await factionService.removeCharacterMember(id, characterId);
      setFeedback('Personnage retiré de la faction.');
      await loadData();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors du retrait du personnage.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveNpc = async (npcId) => {
    try {
      setActionLoading(true);
      await factionService.removeNpcMember(id, npcId);
      setFeedback('PNJ retiré de la faction.');
      await loadData();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors du retrait du PNJ.');
    } finally {
      setActionLoading(false);
    }
  };

  const startRoleEdition = (characterId, currentRole) => {
    if (!faction?.canEdit) return;
    setEditingRoleFor(characterId);
    setRoleDraft(currentRole || '');
  };

  const cancelRoleEdition = () => {
    setEditingRoleFor(null);
    setRoleDraft('');
  };

  const saveRoleEdition = async (characterId) => {
    const nextRole = roleDraft;
    try {
      setActionLoading(true);
      await factionService.updateCharacterRole(id, characterId, nextRole);
      setFaction((prevFaction) => {
        if (!prevFaction) return prevFaction;
        return {
          ...prevFaction,
          characters: (prevFaction.characters || []).map((member) =>
            member.id === characterId ? { ...member, roleRp: nextRole } : member
          ),
        };
      });
      setFeedback('Rôle RP mis à jour.');
      setEditingRoleFor(null);
      setRoleDraft('');
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors de la mise à jour du rôle RP.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptApplication = async (applicationId) => {
    try {
      setActionLoading(true);
      await factionService.acceptApplication(id, applicationId);
      setFeedback('Candidature acceptée.');
      await loadData();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors de l’acceptation de la candidature.');
    } finally {
      setActionLoading(false);
    }
  };

  const openMemberNoteModal = (member, memberType) => {
    setNoteModal({ open: true, member, memberType });
  };

  const closeMemberNoteModal = () => {
    if (!noteSaving) {
      setNoteModal({ open: false, member: null, memberType: 'character' });
    }
  };

  const saveMemberNote = async (memberNote) => {
    if (!noteModal.member) return;
    try {
      setNoteSaving(true);
      if (noteModal.memberType === 'npc') {
        await factionService.updateNpcMemberNote(id, noteModal.member.id, memberNote);
        setFaction((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            npcs: (prev.npcs || []).map((npc) =>
              npc.id === noteModal.member.id ? { ...npc, memberNote } : npc
            ),
          };
        });
      } else {
        await factionService.updateCharacterMemberNote(id, noteModal.member.id, memberNote);
        setFaction((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            characters: (prev.characters || []).map((character) =>
              character.id === noteModal.member.id ? { ...character, memberNote } : character
            ),
          };
        });
      }
      setFeedback('Informations du membre enregistrées.');
      setNoteModal({ open: false, member: null, memberType: 'character' });
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors de l’enregistrement des informations.');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleRejectApplication = async (applicationId) => {
    try {
      setActionLoading(true);
      await factionService.rejectApplication(id, applicationId);
      setFeedback('Candidature refusée.');
      await loadData();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Erreur lors du refus de la candidature.');
    } finally {
      setActionLoading(false);
    }
  };

  const headquartersForumPath = faction?.headquartersForum?.slug
    ? `/forums/${faction.headquartersForum.slug}`
    : faction?.headquartersForum?.id
      ? `/forums/${faction.headquartersForum.id}`
      : null;

  const memberTabs = useMemo(() => {
    const tabs = [
      {
        id: 'characters',
        label: 'Personnages',
        count: faction?.membersCount?.characters || 0,
      },
      {
        id: 'npcs',
        label: 'PNJ',
        count: faction?.membersCount?.npcs || 0,
      },
    ];

    if (faction?.canEdit || showApplySection) {
      tabs.push({
        id: 'applications',
        label: 'Candidatures',
        count: applications.length,
      });
    }

    return tabs;
  }, [faction, applications.length, showApplySection]);

  if (loading) {
    return (
      <Layout wide>
        <Loading message="Chargement de la faction..." />
      </Layout>
    );
  }

  if (error || !faction) {
    return (
      <Layout wide>
        <ErrorMessage message={error || 'Faction introuvable'} onRetry={loadData} />
      </Layout>
    );
  }

  return (
    <Layout wide>
      <div className={styles.contentWide}>
        <Breadcrumb items={[{ name: 'Accueil', url: '/', icon: 'home' }, { name: 'Factions', url: '/factions' }, { name: faction.name, url: null }]} />

        <section className={styles.detailHero}>
          <div className={styles.detailVisual}>
            {faction.logo ? (
              <img src={faction.logo} alt={faction.name} className={styles.detailLogo} />
            ) : (
              <div className={styles.detailLogoPlaceholder}>{faction.name.slice(0, 2).toUpperCase()}</div>
            )}
          </div>

          <div className={styles.detailMain}>
            <div className={styles.detailHeader}>
              <h1>{faction.name}</h1>
              <span className={`${styles.detailStatus} ${styles[faction.status]}`}>{getStatusLabel(faction.status)}</span>
            </div>
            <p className={styles.detailSubtitle}>
              Univers: <strong>{faction.universe?.name || 'N/A'}</strong> — Fondateur: <strong>{faction.founder?.pseudo || 'N/A'}</strong>
            </p>
            <div className={styles.detailTags}>
              {faction.alignment && (
                <span className={`${styles.alignmentBadge} ${styles[`alignment${alignmentMeta.tone}`]}`}>
                  {alignmentMeta.label}
                </span>
              )}
              {faction.scope && (
                <span className={styles.scopeBadge}>
                  <ScopeIcon icon={scopeMeta.icon} />
                  {scopeMeta.label}
                </span>
              )}
            </div>
            {headquartersForumPath && (
              <Link to={headquartersForumPath} className={styles.hqLink}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Quartier général : {faction.headquartersForum.name}
              </Link>
            )}
            <div className={styles.detailActions}>
              <button className={styles.secondaryBtn} onClick={() => navigate('/factions')}>Retour</button>
              {faction.canEdit && (
                <button className={styles.primaryBtn} onClick={() => navigate(`/factions/${id}/edit`)}>
                  Modifier la faction
                </button>
              )}
              {showApplySection && (
                <button
                  className={styles.primaryBtn}
                  onClick={() => {
                    setActiveMemberTab('applications');
                    const membersSection = document.getElementById('faction-members-section');
                    if (membersSection) {
                      membersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  Postuler
                </button>
              )}
            </div>
          </div>

          {faction.icon && (
            <div className={styles.detailIconWrap}>
              <img src={faction.icon} alt={`Icône ${faction.name}`} className={styles.detailIcon} />
            </div>
          )}
        </section>

        {feedback && <p className={styles.detailFeedback}>{feedback}</p>}

        <section className={styles.detailStats}>
          <article className={styles.statCard}>
            <h3>Personnages</h3>
            <p>{faction.stats?.characters ?? faction.membersCount?.characters ?? 0}</p>
          </article>
          <article className={styles.statCard}>
            <h3>PNJ</h3>
            <p>{faction.stats?.npcs ?? faction.membersCount?.npcs ?? 0}</p>
          </article>
          <article className={styles.statCard}>
            <h3>Threads RP</h3>
            <p>{faction.stats?.threads ?? 0}</p>
          </article>
          <article className={styles.statCard}>
            <h3>Posts RP</h3>
            <p>{faction.stats?.posts ?? 0}</p>
          </article>
          <article className={styles.statCard}>
            <h3>Missions / events</h3>
            <p>{faction.stats?.rpActivities ?? 0}</p>
          </article>
        </section>

        {sections.length > 0 && (
          <section className={styles.sectionsPanel}>
            <div className={styles.sectionTabs} role="tablist">
              {sections.map((section) => {
                const sectionKey = section.id ?? section.title;
                return (
                  <button
                    key={sectionKey}
                    type="button"
                    role="tab"
                    aria-selected={String(activeSectionId) === String(sectionKey)}
                    className={`${styles.sectionTab} ${String(activeSectionId) === String(sectionKey) ? styles.sectionTabActive : ''}`}
                    onClick={() => setActiveSectionId(sectionKey)}
                  >
                    {section.title}
                  </button>
                );
              })}
            </div>
            <div className={styles.sectionTabContent} role="tabpanel">
              <ExpandableRichText
                html={activeSection?.content}
                collapsedLines={8}
                emptyMessage="Aucun contenu pour cette section."
              />
            </div>
          </section>
        )}

        <section className={styles.rpScenesPanel}>
          <h2>Scènes RP de la faction</h2>
          <FactionRpScenesPanel
            factionId={faction.id}
            rpArcs={faction.rpArcs || []}
            rpThreads={faction.rpThreads || []}
            canOrganize={faction.canOrganizeRpScenes}
            onOrganizationSaved={(result) => {
              setFaction((prev) => ({
                ...prev,
                rpArcs: result.rpArcs || [],
                rpThreads: result.rpThreads || [],
              }));
              setFeedback('Organisation des scènes RP enregistrée.');
            }}
          />
        </section>

        <section className={styles.membersPanel} id="faction-members-section">
          <div className={styles.membersPanelHeader}>
            <h2>Membres</h2>
            <input
              className={styles.input}
              value={memberSearch}
              onChange={(event) => setMemberSearch(event.target.value)}
              placeholder="Rechercher..."
            />
          </div>

          <div className={styles.memberTabs} role="tablist">
            {memberTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeMemberTab === tab.id}
                className={`${styles.memberTab} ${activeMemberTab === tab.id ? styles.memberTabActive : ''}`}
                onClick={() => setActiveMemberTab(tab.id)}
              >
                {tab.label}
                <span className={styles.memberTabCount}>{tab.count}</span>
              </button>
            ))}
          </div>

          {activeMemberTab === 'characters' && (
            <div className={styles.memberTabPanel} role="tabpanel">
              {faction.canEdit && (
                <div className={styles.inlineForm}>
                  <input
                    className={styles.input}
                    value={addCharacterSearch}
                    onChange={(event) => setAddCharacterSearch(event.target.value)}
                    placeholder="Rechercher dans la sélection..."
                  />
                  <select value={selectedCharacter} onChange={(e) => setSelectedCharacter(e.target.value)} className={styles.input}>
                    <option value="">Sélectionner un personnage</option>
                    {availableCharacters.map((character) => (
                      <option key={character.id} value={character.id}>{character.name}</option>
                    ))}
                  </select>
                  <button className={styles.primaryBtn} onClick={handleAddCharacter} disabled={actionLoading}>
                    Ajouter
                  </button>
                </div>
              )}
              {filteredFactionCharacters.length === 0 ? (
                <p className={styles.memberMeta}>Aucun personnage membre ne correspond à la recherche.</p>
              ) : (
                <div className={styles.memberList}>
                  {filteredFactionCharacters.map((character) => (
                    <FactionMemberCard
                      key={character.id}
                      member={character}
                      memberType="character"
                      factionCanEdit={faction.canEdit}
                      editingRoleFor={editingRoleFor}
                      roleDraft={roleDraft}
                      onRoleDraftChange={setRoleDraft}
                      onStartRoleEdition={startRoleEdition}
                      onCancelRoleEdition={cancelRoleEdition}
                      onSaveRoleEdition={saveRoleEdition}
                      actionLoading={actionLoading}
                      onEditNote={(member) => openMemberNoteModal(member, 'character')}
                      onLeave={handleRemoveCharacter}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeMemberTab === 'npcs' && (
            <div className={styles.memberTabPanel} role="tabpanel">
              {faction.canEdit && (
                <>
                  <p className={styles.memberMeta}>
                    Crée un PNJ de faction (dédié à cette fiche, absent de « Mes PNJ ») ou ajoute un PNJ déjà présent dans ton compte.
                  </p>
                  <div className={`${styles.inlineForm} ${styles.npcInlineForm}`}>
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={openCreateNpcModal}
                      disabled={actionLoading}
                    >
                      Créer un PNJ
                    </button>
                    <select value={selectedNpc} onChange={(e) => setSelectedNpc(e.target.value)} className={styles.input}>
                      <option value="">Sélectionner un PNJ</option>
                      {availableNpcs.map((npc) => (
                        <option key={npc.id} value={npc.id}>{npc.name}</option>
                      ))}
                    </select>
                    <button type="button" className={styles.primaryBtn} onClick={handleAddNpc} disabled={actionLoading}>
                      Ajouter
                    </button>
                  </div>
                </>
              )}
              {filteredFactionNpcs.length === 0 ? (
                <p className={styles.memberMeta}>Aucun PNJ membre ne correspond à la recherche.</p>
              ) : (
                <div className={styles.memberList}>
                  {filteredFactionNpcs.map((npc) => (
                    <FactionMemberCard
                      key={npc.id}
                      member={npc}
                      memberType="npc"
                      actionLoading={actionLoading}
                      onEditNote={(member) => openMemberNoteModal(member, 'npc')}
                      onLeave={handleRemoveNpc}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeMemberTab === 'applications' && (faction.canEdit || showApplySection) && (
            <div className={styles.memberTabPanel} role="tabpanel">
              {faction.canEdit && applications.length > 0 && (
                <div className={styles.candidatureBlock}>
                  <h3 className={styles.candidatureSubTitle}>Demandes en attente</h3>
                  <ul className={styles.list}>
                    {applications.map((application) => (
                      <li key={application.id} className={styles.listItem}>
                        <div>
                          <strong>{application.character?.name || 'Personnage inconnu'}</strong>
                          {application.character?.user?.pseudo && (
                            <p className={styles.memberMeta}>Joueur : {application.character.user.pseudo}</p>
                          )}
                        </div>
                        <div className={styles.detailActions}>
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => handleAcceptApplication(application.id)}
                            disabled={actionLoading}
                          >
                            Accepter
                          </button>
                          <button
                            type="button"
                            className={styles.dangerBtn}
                            onClick={() => handleRejectApplication(application.id)}
                            disabled={actionLoading}
                          >
                            Refuser
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {faction.canEdit && applications.length > 0 && showApplySection && (
                <hr className={styles.candidatureDivider} aria-hidden="true" />
              )}

              {showApplySection && (
                <div className={styles.candidatureBlock}>
                  {faction.canEdit && <h3 className={styles.candidatureSubTitle}>Envoyer une candidature</h3>}
                  {faction.status !== 'open' && (
                    <p className={styles.memberMeta}>Les candidatures sont fermées pour cette faction.</p>
                  )}
                  <div className={styles.inlineForm}>
                    <input
                      className={styles.input}
                      value={applicantSearch}
                      onChange={(event) => setApplicantSearch(event.target.value)}
                      placeholder="Rechercher mon personnage..."
                      disabled={faction.status !== 'open'}
                    />
                    <select
                      value={selectedApplicantCharacter}
                      onChange={(event) => setSelectedApplicantCharacter(event.target.value)}
                      className={styles.input}
                      disabled={faction.status !== 'open'}
                    >
                      <option value="">Sélectionner un personnage</option>
                      {applicableCharacters.map((character) => (
                        <option key={character.id} value={character.id}>
                          {character.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className={styles.primaryBtn}
                      onClick={handleApplyToFaction}
                      disabled={actionLoading || faction.status !== 'open' || applicableCharacters.length === 0}
                    >
                      Postuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <FactionMemberNoteModal
        open={noteModal.open}
        title={
          noteModal.member
            ? `Informations — ${noteModal.member.name}`
            : 'Informations du membre'
        }
        initialValue={noteModal.member?.memberNote || ''}
        saving={noteSaving}
        onClose={closeMemberNoteModal}
        onSave={saveMemberNote}
      />

      {faction?.canEdit && createNpcModalOpen && (
        <div
          className={styles.modalOverlay}
          onClick={() => !actionLoading && setCreateNpcModalOpen(false)}
          role="presentation"
        >
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="faction-create-npc-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 id="faction-create-npc-title" className={styles.modalTitle}>
                Créer un PNJ pour cette faction
              </h2>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => !actionLoading && setCreateNpcModalOpen(false)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.memberMeta}>
                Ce PNJ est rattaché à l’univers de la faction lorsqu’il est défini, enregistré comme PNJ de faction
                (il n’apparaît pas dans « Mes PNJ ») et ajouté aux membres PNJ de cette fiche.
              </p>
              <div className={styles.createNpcModalFields}>
                <label className={styles.modalField}>
                  <span className={styles.modalLabel}>Nom</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={newNpcName}
                    onChange={(e) => setNewNpcName(e.target.value)}
                    placeholder="Nom du PNJ *"
                    disabled={actionLoading}
                    autoComplete="off"
                  />
                </label>
                <label className={styles.modalField}>
                  <span className={styles.modalLabel}>Rôle / fonction</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={newNpcOccupation}
                    onChange={(e) => setNewNpcOccupation(e.target.value)}
                    placeholder="Optionnel"
                    disabled={actionLoading}
                    autoComplete="off"
                  />
                </label>
                <div className={styles.modalField}>
                  <span className={styles.modalLabel}>Portrait</span>
                  <div className={styles.npcAvatarUploadRow}>
                    <input
                      ref={createNpcAvatarInputRef}
                      type="file"
                      accept="image/*"
                      className={styles.npcAvatarFileInput}
                      disabled={actionLoading || uploadingNpcAvatar}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleNpcAvatarFile(f);
                      }}
                    />
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      disabled={actionLoading || uploadingNpcAvatar}
                      onClick={() => createNpcAvatarInputRef.current?.click()}
                    >
                      {uploadingNpcAvatar ? 'Upload…' : 'Choisir une image'}
                    </button>
                    {newNpcAvatarUrl && (
                      <button
                        type="button"
                        className={styles.modalBtnCancel}
                        disabled={actionLoading || uploadingNpcAvatar}
                        onClick={() => setNewNpcAvatarUrl('')}
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                  {newNpcAvatarUrl && (
                    <div className={styles.npcAvatarPreview}>
                      <img src={newNpcAvatarUrl} alt="" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.modalBtnCancel}
                onClick={() => setCreateNpcModalOpen(false)}
                disabled={actionLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className={styles.modalBtnConfirm}
                onClick={handleCreateAndAddNpc}
                disabled={actionLoading || uploadingNpcAvatar || !newNpcName.trim()}
              >
                {actionLoading ? 'Création...' : 'Créer et ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FactionDetail;
